import styled from "styled-components";
import { FiChevronLeft, FiSearch, FiX } from "react-icons/fi";
import { LuArrowLeft } from "react-icons/lu";

import { useEffect, useMemo, useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";

import FlexBox from "../FlexBox";
import { DAVYS_GREY } from "../colors";
import { Body2, H4 } from "../Headings";
import axiosInstance from "../../../axiosInstance";
import URL from "../../../urls";
import AiChatHistoryGroupedList from "./AiChatHistoryGroupedList";
import AiChatHistoryEmptyState from "./AiChatHistoryEmptyState";
import {
  getLastChatLabel,
  groupHistoryByDate,
  mergeUniqueConversations,
  normalizeConversations,
  PAGINATION_LIMIT,
  SEARCH_DEBOUNCE_MS,
} from "./aiChatHelpers";
import useMobileView from "../../../hooks/useMobileView";

const Container = styled(FlexBox)`
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Header = styled(FlexBox)`
  position: relative;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1rem;
  border-bottom: 1px solid ${DAVYS_GREY[300]};
  flex-shrink: 0;
`;

const BackCta = styled.button`
  position: absolute;
  left: 1rem;

  display: flex;
  align-items: center;
  gap: 0.25rem;

  border: none;
  background: transparent;
  cursor: pointer;

  color: ${DAVYS_GREY[800]};
  font-size: 0.875rem;
`;

const SearchSection = styled.div`
  padding: 1rem;
  flex-shrink: 0;
  background: white;
`;

const SearchInputContainer = styled(FlexBox)`
  width: 100%;
  align-items: center;
  gap: 0.5rem;

  padding: 0.75rem;
  border: 1px solid ${DAVYS_GREY[400]};
  border-radius: 0.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;

  font-size: 0.875rem;
  font-family: "Quicksand", sans-serif;
  color: var(--accent-800);

  &::placeholder {
    color: ${DAVYS_GREY[600]};
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: ${DAVYS_GREY[600]};

  &:hover {
    color: ${DAVYS_GREY[800]};
  }
`;

const ScrollableContent = styled(FlexBox)`
  flex: 1;
  min-height: 0;
  flex-direction: column;
  overflow-y: auto;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const PaginationTrigger = styled.div`
  width: 100%;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${DAVYS_GREY[700]};
  font-size: 0.75rem;
`;

const AiChatHistory = ({
  onBack,
  onStart,
  onOpenConversation,
  openingConversationId,
}) => {
  const isMobile = useMobileView();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);

  const scrollableContentRef = useRef(null);
  const paginationTriggerRef = useRef(null);
  const isPaginationRequestPendingRef = useRef(false);

  const isSearchPending = search.trim() !== debouncedSearch;

  useEffect(() => {
    const trimmedSearch = search.trim();

    if (trimmedSearch === debouncedSearch) {
      return;
    }

    const timerId = setTimeout(() => {
      setPage(previousPage => (previousPage === 1 ? previousPage : 1));
      setHasMorePages(false);
      isPaginationRequestPendingRef.current = false;
      setDebouncedSearch(trimmedSearch);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [search, debouncedSearch]);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const fetchConversations = async () => {
      const isInitialPage = page === 1;

      if (isInitialPage) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const response = await axiosInstance.get(URL.aiChatConversations, {
          signal: controller.signal,
          params: {
            page,
            limit: PAGINATION_LIMIT,
            ...(debouncedSearch ? { chat_keyword: debouncedSearch } : {}),
          },
        });

        const conversations = response?.data?.data || [];
        const normalizedConversations = normalizeConversations(conversations);

        const currentPage = Number(response?.data?.page || page);
        const totalPages = Number(response?.data?.total_pages || 1);

        if (mounted) {
          setHistory(previousHistory =>
            isInitialPage
              ? normalizedConversations
              : mergeUniqueConversations(
                  previousHistory,
                  normalizedConversations
                )
          );

          setHasMorePages(currentPage < totalPages);
        }
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        Bugsnag.notify(error);

        if (mounted) {
          if (page === 1) {
            setHistory([]);
          }

          setHasMorePages(false);
        }
      } finally {
        if (mounted) {
          if (page === 1) {
            setLoading(false);
          } else {
            setLoadingMore(false);
          }

          isPaginationRequestPendingRef.current = false;
        }
      }
    };

    fetchConversations();

    return () => {
      controller.abort();
      mounted = false;
    };
  }, [debouncedSearch, page]);

  useEffect(() => {
    if (!hasMorePages || loading || loadingMore || isSearchPending) {
      return;
    }

    if (!paginationTriggerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;

        if (!entry?.isIntersecting) return;
        if (isPaginationRequestPendingRef.current) return;

        isPaginationRequestPendingRef.current = true;

        setPage(previousPage => previousPage + 1);
      },
      {
        root: scrollableContentRef.current,
        rootMargin: "0px 0px 120px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(paginationTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMorePages, isSearchPending, loading, loadingMore]);

  const groupedHistory = useMemo(() => groupHistoryByDate(history), [history]);

  const noSearchResults =
    debouncedSearch && !loading && !isSearchPending && history.length === 0;

  return (
    <Container>
      <Header>
        <BackCta onClick={onBack}>
          {isMobile ? (
            <LuArrowLeft size={20} color={DAVYS_GREY[800]} />
          ) : (
            <>
              <FiChevronLeft size={16} color={DAVYS_GREY[800]} />
              <span>Back</span>
            </>
          )}
        </BackCta>

        <H4 bold>Chat History</H4>
      </Header>

      <SearchSection>
        <SearchInputContainer>
          <FiSearch size={16} strokeWidth={2} color={DAVYS_GREY[800]} />

          <SearchInput
            type="text"
            placeholder="Search by keyword, client name or ID"
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />

          {search && (
            <ClearButton
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <FiX size={16} strokeWidth={2} />
            </ClearButton>
          )}
        </SearchInputContainer>
      </SearchSection>

      <ScrollableContent ref={scrollableContentRef}>
        {loading || isSearchPending ? (
          <FlexBox
            column
            justify="center"
            align="center"
            height="100%"
            padding="1rem"
            gap="0.5rem"
          >
            <lottie-player
              src="https://assets.theinnerhour.com/compressed/ai-response-loading_1768552608412_4vzb7xt.json"
              background="transparent"
              speed="1.5"
              style={{
                width: "1.5rem",
                height: "1.5rem",
                flexShrink: 0,
              }}
              loop
              autoplay
            />
            <Body2 color={DAVYS_GREY[700]}>Loading...</Body2>
          </FlexBox>
        ) : noSearchResults ? (
          <AiChatHistoryEmptyState noSearchResults onStart={onStart} />
        ) : history.length === 0 ? (
          <AiChatHistoryEmptyState noSearchResults={false} onStart={onStart} />
        ) : (
          <AiChatHistoryGroupedList
            groupedHistory={groupedHistory}
            getLastChatLabel={getLastChatLabel}
            onSelectChat={onOpenConversation}
            loadingConversationId={openingConversationId}
          />
        )}

        {!loading && history.length > 0 && hasMorePages && (
          <PaginationTrigger ref={paginationTriggerRef}>
            {loadingMore
              ? "Loading more conversations..."
              : "Scroll to load more"}
          </PaginationTrigger>
        )}
      </ScrollableContent>
    </Container>
  );
};

export default AiChatHistory;
